import { getSingleAttributeValue } from "@stores/gqlModel/getSingleAttributeValue";
import { IAuthService } from '../platform/services/auth/AuthService';
import { IDataClientService } from '../platform/services/data/DataClientService';
import { getCurrentUserIdToken } from "../utils/user";
import { isConflictResponse, ConflictResponse } from './conflictResolution/types';
import { conflictHandlerRegistry } from './conflictResolution/registry';
import { ConflictError, NoConflictHandlerError, MaxRetriesExceededError } from './conflictResolution/errors';
import { conflictLogger } from './conflictResolution/logger';
import {handleUnauthorizedSession} from "../actions/app/handleUnauthorizedSession";
import {
    GraphQLNetworkError,
    isTransientFetchError,
    isTransientGraphQLHttpStatus,
} from './GraphQLNetworkError';
import {
    reportServerConnectionFailure,
    reportServerConnectionRestored,
} from '@stores/serverConnection/serverAvailability';

/**
 * Options for queryGqlApi
 */
export interface QueryGqlApiOptions {
    /**
     * Enable automatic conflict resolution (default: true)
     */
    autoResolveConflicts?: boolean;

    /**
     * Maximum retry attempts for conflict resolution (default: 3)
     */
    maxConflictRetries?: number;

    /**
     * Disable console and logger noise for conflicts that will be handled by the caller.
     */
    suppressConflictLogging?: boolean;

    /**
     * Retry temporary network/server outages. Defaults to true for queries and false for mutations.
     */
    retryTransientErrors?: boolean;

    /**
     * Maximum retry attempts for temporary network/server outages.
     */
    maxTransientRetries?: number;
}

const DEFAULT_TRANSIENT_RETRY_DELAYS_MS = [500, 900, 1600, 2500];

/**
 * Execute a GraphQL query/mutation using the data client service.
 * Supports automatic conflict resolution for versioned updates.
 * 
 * @param query - GraphQL query/mutation string
 * @param variables - GraphQL variables
 * @param dataClientService - Service to resolve endpoints
 * @param authService - Service to get authentication token
 * @param endpointKey - Key to identify which endpoint to use (e.g., 'main', 'quiz', 'pgServer')
 * @param method - HTTP method (default: 'POST')
 * @param options - Additional options including conflict resolution settings
 */
export const queryGqlApi = async (
    query: any,
    variables: any,
    dataClientService: IDataClientService,
    authService: IAuthService,
    endpointKey: string,
    method: 'POST' | 'GET' = 'POST',
    options: QueryGqlApiOptions = {}
): Promise<any> => {
    const {
        autoResolveConflicts = true,
        maxConflictRetries = 3,
        suppressConflictLogging = false,
    } = options;

    // Capture original input at the start for conflict resolution
    const originalInput = variables.input;
    
    let attemptNumber = 0;
    let currentVariables = variables;
    let lastConflict: ConflictResponse | null = null;

    while (attemptNumber <= maxConflictRetries) {
        // Execute GraphQL request
        const response = await executeGraphQLRequest(
            query,
            currentVariables,
            dataClientService,
            authService,
            endpointKey,
            method,
            options
        );

        // Check for conflict
        if (isConflictResponse(response)) {
            lastConflict = response;
            
            if (!suppressConflictLogging) {
                console.warn(
                    `[Conflict Resolution] Detected conflict for ${response.entityType}#${response.entityId} (attempt ${attemptNumber + 1}/${maxConflictRetries + 1})`
                );
            }

            // Check if we should attempt resolution
            if (!autoResolveConflicts || attemptNumber >= maxConflictRetries) {
                const error = attemptNumber >= maxConflictRetries
                    ? new MaxRetriesExceededError(response, maxConflictRetries)
                    : new ConflictError(response, attemptNumber);
                
                if (!suppressConflictLogging) {
                    conflictLogger.logConflict(response, attemptNumber, false, error.message);
                }
                throw error;
            }

            // Get conflict handler
            const handler = conflictHandlerRegistry.get(response.entityType);
            if (!handler) {
                const error = new NoConflictHandlerError(response);
                if (!suppressConflictLogging) {
                    conflictLogger.logConflict(response, attemptNumber, false, error.message);
                }
                throw error;
            }

            // Check if handler wants to retry
            if (handler.shouldRetry && !handler.shouldRetry(response, attemptNumber)) {
                const error = new ConflictError(
                    response,
                    attemptNumber,
                    `Handler declined to retry for ${response.entityType}#${response.entityId}`
                );
                if (!suppressConflictLogging) {
                    conflictLogger.logConflict(response, attemptNumber, false, error.message);
                }
                throw error;
            }

            try {
                // Resolve conflict
                const currentState = response.currentState || await handler.refetch({
                    entityId: response.entityId,
                    dataClientService,
                    authService
                });

                console.log('[conflict] currentState', currentState)
                const mergedChanges = handler.merge({
                    currentState,
                    intendedChanges: originalInput,
                    originalVersion: response.expectedVersion
                });

                // Update variables for retry
                currentVariables = {
                    ...currentVariables,
                    input: {
                        ...mergedChanges,
                        id: response.entityId,
                        version: currentState.version
                    }
                };

                attemptNumber++;
                continue;
            } catch (error) {
                const errorMessage = error instanceof Error ? error.message : String(error);
                if (!suppressConflictLogging) {
                    conflictLogger.logConflict(response, attemptNumber, false, errorMessage);
                }
                throw new ConflictError(
                    response,
                    attemptNumber,
                    `Failed to resolve conflict: ${errorMessage}`
                );
            }
        }

        // Success - log if this was a retry
        if (!suppressConflictLogging && attemptNumber > 0 && lastConflict) {
            conflictLogger.logConflict(lastConflict, attemptNumber, true);
        }

        return response;
    }

    throw new Error('Conflict resolution exceeded maximum retries');
};

/**
 * Internal function to execute a GraphQL request.
 * Separated for clarity and to avoid duplication.
 */
async function executeGraphQLRequest(
    query: string,
    variables: any,
    dataClientService: IDataClientService,
    authService: IAuthService,
    endpointKey: string,
    method: 'POST' | 'GET',
    options: QueryGqlApiOptions
): Promise<any> {
    const endpoint = dataClientService.getHttpEndpointByKey(endpointKey);
    const token = await getCurrentUserIdToken(authService);
    
    if (!token) {
        await handleUnauthorizedSession(new Error(`[Auth] Missing token for ${endpointKey} GraphQL request`));
        return new Promise<never>(() => {});
    }

    const retryTransientErrors = options.retryTransientErrors ?? !isGraphQLMutation(query);
    const maxTransientRetries = options.maxTransientRetries ?? DEFAULT_TRANSIENT_RETRY_DELAYS_MS.length;
    let response: Response;

    for (let retryAttempt = 0; ; retryAttempt++) {
        try {
            response = await fetch(endpoint, {
                method: method,
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: token,
                },
                body: JSON.stringify({
                    query: query,
                    variables: variables,
                }),
            });

            if (isTransientGraphQLHttpStatus(response.status)) {
                throw new GraphQLNetworkError(
                    `GraphQL request failed with temporary status ${response.status}`,
                    endpointKey
                );
            }

            reportServerConnectionRestored();
            break;
        } catch (error) {
            const networkError = isTransientFetchError(error)
                ? new GraphQLNetworkError('Failed to reach GraphQL server', endpointKey, error)
                : error;

            if (
                retryTransientErrors
                && isTransientFetchError(networkError)
                && retryAttempt < maxTransientRetries
            ) {
                await delay(getTransientRetryDelay(retryAttempt));
                continue;
            }

            if (isTransientFetchError(networkError)) {
                reportServerConnectionFailure(networkError);
            }

            throw networkError;
        }
    }

    let responseBody: any = undefined;

    try {
        responseBody = await response.json();
    } catch (error) {
        responseBody = undefined;
    }

    if (response.status === 401 || isUnauthorizedGraphQLErrorResponse(responseBody)) {
        await handleUnauthorizedSession(
            new Error(`[Auth] Unauthorized ${endpointKey} GraphQL request (${response.status})`)
        );
        return new Promise<never>(() => {});
    }

    if (!response.ok) {
        throw new Error(responseBody?.message || `GraphQL request failed with status ${response.status}`);
    }
    
    if (responseBody.errors) {
        throw new Error(JSON.stringify(responseBody.errors));
    }

    return getSingleAttributeValue(responseBody.data);
}

function isGraphQLMutation(query: unknown): boolean {
    return /\bmutation\b/i.test(String(query));
}

function getTransientRetryDelay(retryAttempt: number): number {
    return DEFAULT_TRANSIENT_RETRY_DELAYS_MS[Math.min(
        retryAttempt,
        DEFAULT_TRANSIENT_RETRY_DELAYS_MS.length - 1
    )];
}

function delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
}

function isUnauthorizedGraphQLErrorResponse(responseBody: any): boolean {
    if (!Array.isArray(responseBody?.errors)) {
        return false;
    }

    return responseBody.errors.some((error: any) => {
        const message = String(error?.message || '').toLowerCase();
        const errorType = String(error?.errorType || error?.extensions?.code || '').toLowerCase();

        return message.includes('unauthorized')
            || message.includes('not authorized')
            || errorType.includes('unauthorized')
            || errorType.includes('notauthorized');
    });
}
